import { Box, Flex, SimpleGrid, Spinner, Text,Spacer,HStack,VStack,Button } from '@chakra-ui/react'
import { motion, useReducedMotion } from 'framer-motion'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useState } from "react"
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Customized
} from 'recharts'
import { fetchRecruitmentReports, fetchScreeningApplications, fetchApplicationBreakdown } from '../../api/recruitment/recruitment'
import ChartCard, { ChartTooltip, PieGradientDefs } from '../AdminAnalytics/ChartCard'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
const MotionSimpleGrid = motion(SimpleGrid)
const MotionBox = motion(Box)
import { Menu, MenuButton, MenuList, MenuItem } from '@chakra-ui/react'
const CHART_COLORS = ['#378AFF','#FFEC21','#9552EA','#FFA32F',  '#93F03B', '#f54f52']
import { keepPreviousData } from '@tanstack/react-query'
import RecruitmentStatCards from './RecruitmentStatCards'

const RADIAN2= Math.PI / 180

const renderHiringRatioLabel = (props: any) => {
  const { cx, cy, midAngle, outerRadius, percent, name, value } = props

  if (name !== "Hired" || !value) return null

  // place text on the hired slice's center angle,
  // but closer to the center of the pie
  const r = outerRadius * 0.72

  const x = cx + r * Math.cos(-midAngle * RADIAN2)
  const y = cy + r * Math.sin(-midAngle * RADIAN2)

  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize={12}
      fontWeight={800}
      fill="black"
    >
      {`${Math.round((percent || 0) * 100)}%`}
    </text>
  )
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
}
const RADIAN = Math.PI / 180

const formatNumber = (v: number) => v.toLocaleString()

const renderDonutLabel = (props: any) => {
  const { cx, cy, midAngle, outerRadius, value, percent } = props

  const sx = cx + (outerRadius + 6) * Math.cos(-midAngle * RADIAN)
  const sy = cy + (outerRadius + 6) * Math.sin(-midAngle * RADIAN)

  const mx = cx + (outerRadius + 18) * Math.cos(-midAngle * RADIAN)
  const my = cy + (outerRadius + 18) * Math.sin(-midAngle * RADIAN)

  const isRight = Math.cos(-midAngle * RADIAN) >= 0
  const ex = mx + (isRight ? 18 : -18)
  const ey = my

  const anchor = isRight ? 'start' : 'end'
  const pct = Math.round((percent || 0) * 100)



  return (
    <g>
      <path
        d={`M${sx},${sy} L${mx},${my} L${ex},${ey}`}
        fill="none"
        stroke="#B8C0CC"
        strokeWidth={1}
      />
      <text
        x={ex + (isRight ? 4 : -4)}
        y={ey}
        textAnchor={anchor}
        dominantBaseline="middle"
        fontSize={12}
        fill="#4A5568"
      >
        {`${formatNumber(value)} (${pct}%)`}
      </text>
    </g>
  )
}


const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
}


/* ── Empty state shown inside chart cards when there's no data ── */
const EmptyChart: React.FC<{ icon: React.ReactNode; title: string; subtitle: string }> = ({ icon, title, subtitle }) => (
  <Flex direction="column" align="center" justify="center" h="100%" w="100%" gap="12px">
    <Flex w="56px" h="56px" borderRadius="14px" bg="#F4F4F6" align="center" justify="center">
      {icon}
    </Flex>
    <Box textAlign="center">
      <Text fontSize="14px" fontWeight="600" color="solid.600" mb="2px">{title}</Text>
      <Text fontSize="12px" fontWeight="400" color="solid.300" maxW="240px">{subtitle}</Text>
    </Box>
  </Flex>
)

/* ── Tiny inline SVG icons for empty states ── */
const BarChartIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="12" width="4" height="9" rx="1" fill="#C8C9D0" />
    <rect x="10" y="7" width="4" height="14" rx="1" fill="#C8C9D0" />
    <rect x="17" y="3" width="4" height="18" rx="1" fill="#C8C9D0" />
  </svg>
)

const PieChartIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8V12l6.93 4a7.96 7.96 0 01-6.93 4z" fill="#C8C9D0" />
  </svg>
)

const TrendIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M3 17l6-6 4 4 8-8" stroke="#C8C9D0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 7h4v4" stroke="#C8C9D0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)


const axisProps = { fontSize: 11, fill: '#808897' }

const ReportsDashboard: React.FC = () => {
  const prefersReducedMotion = useReducedMotion()
  const { t } = useTranslation()
  const [period, setPeriod] = useState<'weekly' | 'monthly' | 'yearly' | 'all'>('weekly')

const reports = useQuery({
  queryKey: ['recruitment-reports'],
  queryFn: fetchRecruitmentReports,
  staleTime: 60000,
})

const breakdownQuery = useQuery({
  queryKey: ['application-breakdown', period],
  queryFn: () => fetchApplicationBreakdown(period),
  placeholderData: keepPreviousData,
})

  const screeningQ = useInfiniteQuery({
    queryKey: ['screening-applications'],
    queryFn: ({ pageParam }) => fetchScreeningApplications(pageParam),
    initialPageParam: null,
    enabled: reports.isSuccess, 
    getNextPageParam: (lastPage: any) => {
      if (!lastPage?.next) return undefined
      const url = new URL(lastPage.next)
      return url.searchParams.get('cursor') ?? undefined 
    },
  })

  if (reports.isLoading) {
    return (
      <Flex justify="center" align="center" minH="400px" w="100%">
        <Spinner size="lg" color="primary.100" />
      </Flex>
    )
  }

  if (reports.isError) {
    return (
      <Flex justify="center" align="center" minH="400px" w="100%">
        <Text color="error.200" fontSize="14px">{t('recruitment.reports.loadFailed')}</Text>
      </Flex>
    )
  }


const screeningItems = screeningQ.data?.pages?.flatMap((p: any) => p.results) ?? []
  const data = reports.data || {
    time_to_hire: 0,
    source_breakdown: [],
    pipeline_summary: [],
    funnel_data: [],
    trend_data: [],
  }


    const pad2 = (n: number) => String(n).padStart(2, "0")

const dateKey = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`

const fmtDay = (d: Date) =>
  d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit" })

const fmtTime = (iso: string) => {
  const d = new Date(iso)
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

  const totalCandidates = data.pipeline_summary?.reduce((sum: number, item: any) => sum + item.count, 0) || 0
  const openPositions = data.pipeline_summary?.filter((item: any) => !['hired', 'rejected'].includes(item.status))
    .reduce((sum: number, item: any) => sum + item.count, 0) || 0

  const hasPipelineData = (data.pipeline_summary || []).length > 0
  const pipelineTotal = data.pipeline_summary?.reduce((s: number, i: any) => s + i.count, 0) || 1

const breakdownData = breakdownQuery.data ?? data

const applicationBreakdown = (breakdownData.source_breakdown || []).map((d: any) => ({
  source: d.source,
  applied: d.count ?? 0,
  hired: d.hired ?? 0,
  rejected: d.rejected ?? 0,
}))

const TrendDirectionIcon: React.FC<{ direction?: 'up' | 'down' | 'flat' | null }> = ({ direction }) => {
  if (direction === 'up') {
    return <TrendingUp size={14} color="#16A34A" />
  }

  if (direction === 'down') {
    return <TrendingDown size={14} color="#DC2626" />
  }

  return <Minus size={14} color="#6B7280" />
}

const abMeta = breakdownData.application_breakdown_meta
const pct = abMeta?.pct_change
const direction = abMeta?.direction // 'up' | 'down' | 'flat' | null
const isAll = abMeta?.period === 'all'

const pctText =
  isAll || pct === null || pct === undefined ? 'All time' : `${pct.toFixed(2)}%`

const trendBg =
  direction === 'up'
    ? '#ECFDF3'
    : direction === 'down'
    ? '#FEF2F2'
    : '#F4F4F6'

const trendBorder =
  direction === 'up'
    ? '#BBF7D0'
    : direction === 'down'
    ? '#FECACA'
    : '#EEF1F6'

const trendSentence =
  isAll
    ? `Total applications: ${abMeta?.current?.count ?? 0}`
    : direction === 'up'
      ? `Increase in candidate applications compared to last period.`
      : direction === 'down'
        ? `Decrease in candidate applications compared to last period.`
        : `Candidate applications are flat compared to last period.`
const hasApplicationBreakdown = applicationBreakdown.length > 0


const hiredCount =
  data.pipeline_summary?.find((x: any) => x.status === 'hired')?.count ?? 0

const totalApps = totalCandidates 
const notHiredCount = Math.max(totalApps - hiredCount, 0)

const hiringPieData = [
  { name: 'Hired', value: hiredCount },
  { name: 'Not hired', value: notHiredCount },
]

const normalizedBreakdown = applicationBreakdown.map((d: any) => ({
  ...d,
  applied_v: d.applied || 0,
  hired_v: d.hired || 0,
  rejected_v: d.rejected || 0,

  // constant gaps
  gap1: 0.12,
  gap2: 0.12,
}))

  return (
    <MotionBox
      flexDirection="column"
      gap="16px"
      w="100%"
      pb="24px"
      maxH="83vh"
      variants={containerVariants}
      initial={prefersReducedMotion ? false : 'hidden'}
      animate="visible"
    >   

    <MotionBox variants={itemVariants}>
  <RecruitmentStatCards
    totalCandidates={totalCandidates}
    openPipeline={openPositions}
    totalReviewedCandidates={data.total_reviewed_candidates ?? 0}
    avgTimeToHire={data.time_to_hire || 0}
    totalHiredCandidates={data.total_hired_candidates ?? 0}
    isLoading={reports.isLoading}
  />
</MotionBox>
      <MotionSimpleGrid
        columns={{ base: 1, lg: 5 }}
        spacing="12px"
        pt="12px"
        variants={containerVariants}
      >
        <Box gridColumn={{ base: "span 1", lg: "span 2" }}>
  <ChartCard title="Distribution" isLoading={breakdownQuery.isLoading} h="280px" action={
      <Flex align="center" gap="18px">
        {data.pipeline_summary.map((item: any, index: number) => (
          <Flex key={index} align="center" gap="6px">
            <Box
              w="10px"
              h="10px"
              borderRadius="full"
              bg={CHART_COLORS[index % CHART_COLORS.length]}
            />
            <Text fontSize="12px" color="solid.600" textTransform="capitalize">
              {item.status}
            </Text>
          </Flex>
        ))}
      </Flex>
    }>
    {hasPipelineData ? (
      <Box h="100%">
        <ResponsiveContainer width="100%" height="100%" > 
          <PieChart margin={{ top: 18}}>
            <Customized
              component={() => (
                <PieGradientDefs
                  ids={CHART_COLORS.map((c, i) => ({
                    id: `pipeline-${i}`,
                    color: c,
                  }))}
                />
              )}
            />

            <Pie
              data={data.pipeline_summary}
              dataKey="count"
              nameKey="status"
              cx="50%"
              cy="45%"                 
              innerRadius={62}
              outerRadius={96}
              paddingAngle={4}
              cornerRadius={0}
              stroke="none"
              labelLine={false}        
              label={renderDonutLabel} 
              isAnimationActive={false}
            >
              {data.pipeline_summary.map((_: any, index: number) => (
                <Cell
                  key={index}
                  fill={`url(#pie-grad-pipeline-${index % CHART_COLORS.length})`}
                  style={{
                    filter: `url(#pie-glow-pipeline-${index % CHART_COLORS.length})`,
                  }}
                />
              ))}
            </Pie>

            <Tooltip
              content={
                <ChartTooltip
                  valueFormatter={(v) =>
                    `${v} candidates (${((v / pipelineTotal) * 100).toFixed(0)}%)`
                  }
                />
              }
            />

            
          </PieChart>
        </ResponsiveContainer>
      </Box>
    ) : (
      <EmptyChart
        icon={<PieChartIcon />}
        title="No pipeline data yet"
        subtitle="Stage distribution will show once candidates enter the pipeline"
      />
    )}
  </ChartCard>
</Box>
        <Box gridColumn={{ base: "span 1", lg: "span 2" }}>

<ChartCard title="Currently Screening" isLoading={screeningQ.isLoading} h="280px">
  <Box
    h="100%"
    overflowY="auto"
    pr="4px"
    onScroll={(e) => {
      const el = e.currentTarget
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40
      if (nearBottom && screeningQ.hasNextPage && !screeningQ.isFetchingNextPage) {
        screeningQ.fetchNextPage()
      }
    }}
    sx={{
      "&::-webkit-scrollbar": { width: "6px" },
      "&::-webkit-scrollbar-thumb": { background: "#D7DEEA", borderRadius: "999px" },
    }}
  >
    {screeningItems.length > 0 ? (
      <Flex direction="column" gap="12px">
        {screeningItems.map((a: any, i: number) => {
          const parts = (a.name || "").split(" ")
          const initials = `${parts[0]?.[0] || ""}${parts[1]?.[0] || ""}`.toUpperCase()

          return (
            <Flex
              key={a.id ?? i}
              align="center"
              justify="space-between"
              p="10px"
              borderRadius="12px"
              border="1px solid #EEF1F6"
              bg="#FBFCFE"
            >
              <Flex align="center" gap="10px">
                <Flex
                  w="36px"
                  h="36px"
                  borderRadius="full"
                  align="center"
                  justify="center"
                  bg="#EEF2FF"
                  color="#5d5ecb"
                  fontWeight="700"
                  fontSize="12px"
                >
                  {initials}
                </Flex>

                <Box>
                  <Text fontSize="13px" fontWeight="700" color="solid.900">
                    {a.name}
                  </Text>
                  <Text fontSize="12px" color="solid.500">
                    {a.email}
                  </Text>
                </Box>
              </Flex>

              <Box textAlign="right" w="50%">
                <HStack>
                  <Text>{a.phone || "No phone"}</Text>
                  <Spacer />
                  <VStack spacing={0} alignItems="flex-end">
                    <Text fontSize="11px" color="solid.500">{a.source}</Text>
                    <Text fontSize="11px" color="solid.500">
                      {new Date(a.applied_at).toLocaleDateString(undefined, {
                        month: "short",
                        day: "2-digit",
                        year: "numeric",
                      })}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            </Flex>
          )
        })}

        {screeningQ.isFetchingNextPage && (
          <Text fontSize="12px" color="solid.400" textAlign="center" py="6px">
            Loading more…
          </Text>
        )}
      </Flex>
    ) : (
      <EmptyChart
        icon={<TrendIcon />}
        title="No screening candidates"
        subtitle="Candidates in screening will appear here."
      />
    )}
  </Box>
</ChartCard>
</Box>
        <Box gridColumn={{ base: "span 1", lg: "span 1" }}>
  <ChartCard title="Hiring Ratio" isLoading={reports.isLoading} h="280px">
    {totalApps > 0 ? (
      <Flex h="100%"  justify="space-between" gap="0px" direction="column">
        
        {/* donut */}
        <Box w="100%"  h="100%" minW={0}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
  data={hiringPieData}
  dataKey="value"
  nameKey="name"
  cx="50%"
  cy="45%"
  outerRadius={104}
  paddingAngle={0}
  stroke="white"
  strokeWidth={0}
  cornerRadius={0}
  isAnimationActive={false}
  labelLine={false}
  label={renderHiringRatioLabel}
>
  <Cell fill="#ffb6b6" />
  <Cell fill="#7d7ecf" />
</Pie>

              <Tooltip
                content={
                  <ChartTooltip
                    valueFormatter={(v) => `${v} candidates`}
                  />
                }
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
         
      
      </Flex>
    ) : (
      <EmptyChart
        icon={<PieChartIcon />}
        title="No applications yet"
        subtitle="Hiring ratio will appear once candidates enter the pipeline."
      />
    )}
  </ChartCard>
</Box>

      
      </MotionSimpleGrid>
      <MotionSimpleGrid
        columns={{ base: 1, lg: 5 }}
        columnGap="12px"
        variants={containerVariants}
        pt="12px"
      >
       <Box gridColumn={{ base: "span 1", lg: "span 2" }}>
  <ChartCard
    title="Application Breakdown"
    isLoading={reports.isLoading}
    h="280px"
    action={
      <Menu>
        <MenuButton
          w="100px"
          h="18px"
          as={Button}
          type="button"
          variant="unstyled"
          px="12px"
          borderRadius="999px"
          display="flex"
          alignItems="center"
          gap="8px"
          cursor="pointer"
          userSelect="none"
          onClick={(e) => e.stopPropagation()}
        >
          <HStack>
            <Spacer/>
              <Text fontSize="14px" fontWeight="600" color="solid.700">
            {period === "weekly"
              ? "Weekly"
              : period === "monthly"
              ? "Monthly"
              : period === "yearly"
              ? "Yearly"
              : "All time"}
          </Text>
          <Box as="span" color="solid.500" fontSize="14px" lineHeight="1">
            ▾
          </Box>
          </HStack>
          
        </MenuButton>

        <MenuList p="6px" borderRadius="14px" border="1px solid #EEF1F6">
          <MenuItem
            borderRadius="10px"
            onClick={(e) => {
              e.stopPropagation()
              setPeriod("weekly")
            }}
          >
            This week
          </MenuItem>
          <MenuItem
            borderRadius="10px"
            onClick={(e) => {
              e.stopPropagation()
              setPeriod("monthly")
            }}
          >
            This month
          </MenuItem>
          <MenuItem
            borderRadius="10px"
            onClick={(e) => {
              e.stopPropagation()
              setPeriod("yearly")
            }}
          >
            This year
          </MenuItem>
          <MenuItem
            borderRadius="10px"
            onClick={(e) => {
              e.stopPropagation()
              setPeriod("all")
            }}
          >
            All time
          </MenuItem>
        </MenuList>
      </Menu>
    }
  >
    {hasApplicationBreakdown ? (
      <Flex h="100%" w="100%" gap="14px" >
        {/* Left insight panel */}
        <Flex
          direction="column"
          w={{ base: "42%", lg: "28%" }}
          pr="6px"
          h="94%"
          justify="center"
          gap="10px"
          >
          <Flex align="center" gap="10px" pt="8px">
            <Text fontSize="24px" fontWeight="800" color="solid.900" lineHeight="1">
              {pctText}
            </Text>

            <Flex
              w="28px"
              h="28px"
              borderRadius="999px"
              bg={trendBg}
              align="center"
              justify="center"
              border="1px solid"
              borderColor={trendBorder}
            >
              <TrendDirectionIcon direction={direction} />
            </Flex>
          </Flex>

          <Text fontSize="16px" color="solid.500" lineHeight="1.45">
            {trendSentence}
          </Text>
          <Spacer/>


          {/* Legend bottom-left */}
          <Flex direction="column" gap="0px" pt="8px">
            <Flex align="center" gap="10px">
              <Box w="12px" h="12px" borderRadius="full" bg="#7d7ecf"  />
              <Text fontSize="13px" fontWeight="600" color="solid.800">
                New
              </Text>
            </Flex>

            <Flex align="center" gap="10px">
              <Box w="12px" h="12px" borderRadius="full" bg="#FFB6B6" />
              <Text fontSize="13px" fontWeight="600" color="solid.800">
                Hired
              </Text>
            </Flex>

            <Flex align="center" gap="10px">
              <Box w="12px" h="12px" borderRadius="full" bg="#f54f52" />
              <Text fontSize="13px" fontWeight="600" color="solid.800">
                Rejected
              </Text>
            </Flex>
          </Flex>
        </Flex>

        {/* Right chart panel */}
        <Box flex="1" minW={0} h="100%">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={normalizedBreakdown} margin={{ left: 8, right: 6, top: 12, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ECEFF3" vertical={false} />

              <XAxis
                dataKey="source"
                tick={axisProps}
                axisLine={false}
                tickLine={false}
                interval={0}
                tickMargin={10}
              />

              <YAxis tick={axisProps} axisLine={false} tickLine={false} width={30} allowDecimals={false} />

              <Tooltip
                contentStyle={{
                  borderRadius: "12px",
                  border: "1px solid #E2E8F0",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                  padding: "10px 12px",
                }}
                cursor={{ fill: "rgba(55,138,255,0.06)" }}
              />

              {/* Applied */}
              <Bar dataKey="applied_v" stackId="s" fill= "#7d7ecf" barSize={14} radius={[20, 20, 20, 20]} />

              {/* gap */}
              <Bar dataKey="gap1" stackId="s" fill="transparent" barSize={14} />

              {/* Hired */}
              <Bar dataKey="hired_v" stackId="s" fill="#FFB6B6" barSize={14} radius={[20, 20, 20, 20]} />

              {/* gap */}
              <Bar dataKey="gap2" stackId="s" fill="transparent" barSize={14} />

              {/* Rejected */}
              <Bar dataKey="rejected_v" stackId="s" fill="#f54f52" barSize={14} radius={[20, 20, 20, 20]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Flex>
    ) : (
      <EmptyChart
        icon={<BarChartIcon />}
        title="No application breakdown yet"
        subtitle="Applied, hired, and rejected counts will appear once you have activity"
      />
    )}
  </ChartCard>
</Box>
   <Box gridColumn={{ base: "span 1", lg: "span 3" }}>
  <ChartCard title="Interview Calendar" isLoading={reports.isLoading} h="280px">
    {(data.upcoming_interviews?.length ?? 0) > 0 ? (
      (() => {
        const start = new Date()
        start.setHours(0, 0, 0, 0)

        const daysCount = data.calendar_days ?? 3
        const days = Array.from({ length: daysCount }).map((_, i) => {
          const d = new Date(start)
          d.setDate(start.getDate() + i)
          return d
        })

        const grouped: Record<string, any[]> = {}
        ;(data.upcoming_interviews || []).forEach((itv: any) => {
          const k = dateKey(new Date(itv.scheduled_at))
          grouped[k] = grouped[k] || []
          grouped[k].push(itv)
        })

        return (
          <Flex h="100%" overflowX="auto" overflowY="hidden" gap="18px" pt="6px" pb="6px">
            {days.map((d) => {
              const k = dateKey(d)
              const items = (grouped[k] || []).slice().sort(
                (a, b) => +new Date(a.scheduled_at) - +new Date(b.scheduled_at)
              )

              const isToday = dateKey(new Date()) === k

              return (
                <Box
                  key={k}
                  minW="32%"
                  flex="0 0 auto"
                  border="1px solid"
                  borderColor="#EEF1F6"
                  bg={isToday ? "white" : "white"}
                  borderRadius="14px"
                  p="10px"
                  h="100%"
                  display="flex"
                  flexDirection="column"
                  overflow="hidden"
                >
                  {/* header */}
                  <Flex justify="space-between" align="center" mb="8px">
                    <Text fontSize="12px" fontWeight="700" color="solid.800">
                      {fmtDay(d)}
                    </Text>
                    {isToday && (
                      <Box px="8px" py="2px" borderRadius="999px" bg="rgba(93,94,203,0.12)">
                        <Text fontSize="10px" fontWeight="700" color="primary.100">
                          Today
                        </Text>
                      </Box>
                    )}
                  </Flex>

                  {/* scrollable content */}
                  <Box
                    flex="1"
                    minH={0}
                    overflowY="auto"
                    pr="2px"
                    sx={{
                      "&::-webkit-scrollbar": { width: "6px" },
                      "&::-webkit-scrollbar-thumb": { background: "#D7DEEA", borderRadius: "999px" },
                    }}
                  >
                    {items.length ? (
                      <Flex direction="column" gap="10px" py="5px">
                        {items.map((itv: any, idx: number) => (
                          <Box
                            key={idx}
                            borderRadius="0px"
                            bg="none"
                            borderLeft="3px solid #9552EA"
                            p="8px"
                          >
                            <HStack w = "100%">
                              <Box>
                            <Text fontSize="12px" fontWeight="700" color="solid.900" noOfLines={1}>
                              {itv.candidate_name}
                            </Text>
                            <Text fontSize="11px" color="solid.500" noOfLines={1}>
                              {itv.vacancy || "Vacancy: -"}
                            </Text>
                            <Text fontSize="11px" color="solid.500" noOfLines={1}>
                              {`Type: ${itv.type || "Type: -"}`} - {itv.duration_minutes} minutes
                            </Text>      
                              </Box>
                              
                              <Spacer/>
                              <Box justifySelf="flex-end" alignSelf="flex-start">
                                <Text fontSize="11px" fontWeight="800" color="#1D4ED8">
                                  {fmtTime(itv.scheduled_at)}
                                </Text>
                              </Box>
                            </HStack>
                          </Box>
                        ))}
                      </Flex>
                    ) : (
                      <Text fontSize="11px" color="solid.400">
                        No interviews
                      </Text>
                    )}
                  </Box>
                </Box>
              )
            })}
          </Flex>
        )
      })()
    ) : (
      <EmptyChart
        icon={<TrendIcon />}
        title="No interviews scheduled"
        subtitle="Interviews for the next 7 days will appear here."
      />
    )}
  </ChartCard>
</Box>
      </MotionSimpleGrid>

      
    </MotionBox>
  )
}

export default ReportsDashboard
